// Per-column faceted filter: a funnel button in a table header that opens a
// checkbox list of the column's distinct values. Empty selection = no filter.
// Rendered through a portal with fixed positioning so the table's overflow
// (overflow-hidden Card + overflow-x-auto scroller) can't clip it.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ListFilter } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { cn } from '../lib/utils';

export function ColumnFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const active = selected.size > 0;

  useEscapeKey(open, () => setOpen(false));

  // Outside-click (mousedown, matching the app's popover convention) + close on
  // scroll/resize (the fixed position is computed once at open).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    // Close when the page/table BEHIND the popover scrolls (the popover is
    // fixed-positioned, so it would otherwise detach from its header). But IGNORE
    // scrolls that originate inside the popover's own list — those must scroll the
    // list / let the scrollbar be dragged, not dismiss it.
    const onScroll = (e: Event) => {
      const t = e.target as Node | null;
      if (t && popRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true); // capture: catch inner page scrollers too
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const openMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    const r = btnRef.current!.getBoundingClientRect();
    // Clamp so a near-right-edge column doesn't overflow the viewport.
    const left = Math.min(r.left, window.innerWidth - 13 * 16 - 8);
    setPos({ top: r.bottom + 4, left: Math.max(8, left) });
    setOpen(true);
  };

  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(next);
  };

  return (
    <span className="relative inline-flex shrink-0" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onClick={openMenu}
        aria-label={`Filter ${label}`}
        title={`Filter ${label}`}
        className={cn(
          'ml-1 inline-flex h-4 w-4 items-center justify-center rounded transition-colors hover:bg-accent/60',
          active ? 'text-primary' : 'text-muted-foreground/40 hover:text-muted-foreground',
        )}
      >
        <ListFilter className="h-3 w-3" />
      </button>
      {active && (
        <span className="pointer-events-none absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
      )}
      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
            onClick={(e) => e.stopPropagation()}
            className="z-50 w-52 rounded-md border border-border bg-card text-foreground shadow-md"
          >
            <div className="flex items-center justify-between border-b border-border px-2.5 py-1.5">
              <span className="text-xs font-semibold normal-case tracking-normal">{label}</span>
              <span className="flex gap-2 text-xs">
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => onChange(new Set(options))}
                >
                  All
                </button>
                <button
                  type="button"
                  className="text-muted-foreground hover:underline"
                  onClick={() => onChange(new Set())}
                >
                  Clear
                </button>
              </span>
            </div>
            <div className="max-h-64 overflow-auto py-1">
              {options.map((v) => (
                <label
                  key={v}
                  className="flex cursor-pointer items-center gap-2 px-2.5 py-1 text-sm normal-case tracking-normal hover:bg-accent/40"
                >
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 cursor-pointer accent-primary"
                    checked={selected.has(v)}
                    onChange={() => toggle(v)}
                  />
                  <span className="truncate">{v}</span>
                </label>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </span>
  );
}
